/**
 * Experiments — Phase 5.C
 *
 * Lightweight A/B comparator over historical bets. Pick a control
 * strategy_id and a treatment strategy_id, see side-by-side hit rate,
 * P&L, sample size, plus a two-proportion z-test on the win-rate
 * difference. No experimentation routing yet — this is the inspector
 * you use AFTER running variants in production via separate
 * strategy_ids on the picker side.
 *
 * Reads existing /api/bets feed; pure frontend stats.
 */
import { html, type TemplateResult } from "lit-html";
import { store } from "../data/store";
import type { BetsResponse, Bet } from "../data/types";
import { renderExplainButton } from "./ExplainButton";

interface Variant {
  strategy_id: string;
  bets:        Bet[];
  decided:     number;
  wins:        number;
  losses:      number;
  win_rate:    number | null;
  pnl_units:   number;
  avg_clv:     number | null;
  avg_edge:    number | null;
  sports:      string[];
}

interface ZResult {
  z:          number;
  p_two:      number;
  significant_05: boolean;
  significant_01: boolean;
  control_p:  number;
  treatment_p: number;
  diff_pp:    number;
}

interface ViewState {
  controlId:   string | null;
  treatmentId: string | null;
}
const view: ViewState = {
  controlId:   null,
  treatmentId: null,
};

export function renderExperiments(onChange: () => void): TemplateResult {
  const e = store.get<BetsResponse>("bets");
  if (e.error) return errorView(e.error);
  if (!e.data && e.loading) return skeletonView();
  if (!e.data) return emptyView();

  const allStrategies = uniqueStrategies(e.data.bets);

  // Defaults — most-decided + second-most for first useful comparison
  if (!view.controlId   && allStrategies.length > 0) view.controlId   = allStrategies[0]!;
  if (!view.treatmentId && allStrategies.length > 1) view.treatmentId = allStrategies[1]!;

  const control   = view.controlId   ? buildVariant(e.data.bets, view.controlId)   : null;
  const treatment = view.treatmentId ? buildVariant(e.data.bets, view.treatmentId) : null;
  const z = (control && treatment) ? twoPropZTest(control, treatment) : null;

  return html`
    <div class="exp">
      ${pickerCard(allStrategies, onChange)}

      ${(control && treatment) ? html`
        ${verdictCard(control, treatment, z)}
        <div class="exp-grid">
          ${variantCard(control,   "control",   "is-control")}
          ${variantCard(treatment, "treatment", "is-treatment")}
        </div>
      ` : html`
        <section class="panel" style="padding:48px;text-align:center;">
          <div style="color:var(--text-tertiary);font-size:14px;line-height:1.5;
                       max-width:480px;margin:0 auto;">
            Pick a control and a treatment strategy to compare.
          </div>
        </section>
      `}
    </div>
  `;
}

// =============================================================
// Picker card
// =============================================================
function pickerCard(strategies: string[], onChange: () => void): TemplateResult {
  return html`
    <section class="panel exp-picker">
      <div class="exp-picker__row">
        <div class="exp-picker__field">
          <label class="exp-picker__label">control</label>
          <select class="exp-picker__select"
                  @change=${(e: Event) => {
                    view.controlId = (e.target as HTMLSelectElement).value;
                    onChange();
                  }}>
            ${strategies.map((s) => html`
              <option value=${s} ?selected=${s === view.controlId}>${s}</option>
            `)}
          </select>
        </div>

        <span class="exp-picker__vs">vs</span>

        <div class="exp-picker__field">
          <label class="exp-picker__label">treatment</label>
          <select class="exp-picker__select"
                  @change=${(e: Event) => {
                    view.treatmentId = (e.target as HTMLSelectElement).value;
                    onChange();
                  }}>
            ${strategies.map((s) => html`
              <option value=${s} ?selected=${s === view.treatmentId}>${s}</option>
            `)}
          </select>
        </div>

        <span class="exp-picker__spacer"></span>

        <button class="exp-picker__swap"
                @click=${() => {
                  const prev = view.controlId;
                  view.controlId = view.treatmentId;
                  view.treatmentId = prev;
                  onChange();
                }}
                title="Swap control and treatment">↔ swap</button>
      </div>
    </section>
  `;
}

// =============================================================
// Verdict card
// =============================================================
function verdictCard(c: Variant, t: Variant, z: ZResult | null): TemplateResult {
  const verdict = !z
    ? { label: "no decided bets", cls: "is-idle" }
    : z.significant_01
    ? (z.diff_pp > 0
        ? { label: "TREATMENT WINS", cls: "is-win" }
        : { label: "CONTROL WINS", cls: "is-loss" })
    : z.significant_05
    ? (z.diff_pp > 0
        ? { label: "treatment leans winning", cls: "is-lean-win" }
        : { label: "control leans winning", cls: "is-lean-loss" })
    : { label: "no significant difference", cls: "is-idle" };

  return html`
    <section class="panel exp-verdict">
      <div class="exp-verdict__row">
        <div class="exp-verdict__label-block">
          <span class="exp-verdict__label">verdict</span>
          <span class="exp-verdict__pill ${verdict.cls}">${verdict.label}</span>
        </div>

        ${z ? html`
          <div class="exp-verdict__metric">
            <span class="exp-verdict__metric-label">delta (treatment − control)</span>
            <span class="exp-verdict__metric-value ${z.diff_pp >= 0 ? "is-pos" : "is-neg"}">
              ${z.diff_pp >= 0 ? "+" : ""}${z.diff_pp.toFixed(2)}pp
            </span>
          </div>
          <div class="exp-verdict__metric">
            <span class="exp-verdict__metric-label">z-score</span>
            <span class="exp-verdict__metric-value">${z.z.toFixed(2)}</span>
          </div>
          <div class="exp-verdict__metric">
            <span class="exp-verdict__metric-label">p-value (two-sided)</span>
            <span class="exp-verdict__metric-value ${z.significant_05 ? "is-pos" : ""}">
              ${z.p_two < 0.0001 ? "<0.0001" : z.p_two.toFixed(4)}
            </span>
          </div>
        ` : ""}

        <span class="exp-verdict__spacer"></span>

        ${renderExplainButton({
          label: "Explain experiment",
          context: () => buildExplainContext(c, t, z),
        })}
      </div>
    </section>
  `;
}

// =============================================================
// Variant card
// =============================================================
function variantCard(v: Variant, role: string, cls: string): TemplateResult {
  const wrCls = v.win_rate == null ? "is-idle"
              : v.win_rate >= 60 ? "is-ok"
              : v.win_rate >= 50 ? "is-warn"
              : "is-bad";
  const pnlCls = v.pnl_units > 0 ? "is-pos" : v.pnl_units < 0 ? "is-neg" : "";

  return html`
    <section class="panel exp-variant ${cls}">
      <header class="exp-variant__head">
        <span class="exp-variant__role">${role}</span>
        <h3>${v.strategy_id}</h3>
      </header>
      <div class="exp-variant__sports">
        ${v.sports.length > 0 ? v.sports.map((s) => html`
          <span class="exp-variant__sport-pill">${s}</span>
        `) : html`<span class="exp-variant__sport-pill is-mute">no sports</span>`}
      </div>
      <div class="exp-variant__metrics">
        <div class="exp-variant__metric">
          <span class="exp-variant__metric-label">decided</span>
          <span class="exp-variant__metric-value">${v.decided}</span>
          <span class="exp-variant__metric-sub">
            ${v.bets.length} total · ${v.wins}W ${v.losses}L
          </span>
        </div>
        <div class="exp-variant__metric">
          <span class="exp-variant__metric-label">win rate</span>
          <span class="exp-variant__metric-value">
            <span class="exp-variant__win-pill ${wrCls}">
              ${v.win_rate == null ? "—" : `${v.win_rate.toFixed(1)}%`}
            </span>
          </span>
        </div>
        <div class="exp-variant__metric">
          <span class="exp-variant__metric-label">P&amp;L</span>
          <span class="exp-variant__metric-value ${pnlCls}">
            ${v.pnl_units >= 0 ? "+" : ""}${v.pnl_units.toFixed(2)}u
          </span>
        </div>
        <div class="exp-variant__metric">
          <span class="exp-variant__metric-label">avg CLV</span>
          <span class="exp-variant__metric-value">
            ${v.avg_clv == null ? "—" : `${v.avg_clv >= 0 ? "+" : ""}${v.avg_clv.toFixed(2)}`}
          </span>
        </div>
        <div class="exp-variant__metric">
          <span class="exp-variant__metric-label">avg edge</span>
          <span class="exp-variant__metric-value">
            ${v.avg_edge == null ? "—" : `${v.avg_edge.toFixed(1)}pp`}
          </span>
        </div>
      </div>
    </section>
  `;
}

// =============================================================
// Aggregation + stats
// =============================================================
function uniqueStrategies(bets: Bet[]): string[] {
  const counts: Record<string, number> = {};
  for (const b of bets) {
    const k = b.strategy_id || "unknown";
    counts[k] = (counts[k] ?? 0) + 1;
  }
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])      // most-bets first
    .map(([k]) => k);
}

function buildVariant(bets: Bet[], strategyId: string): Variant {
  const my = bets.filter((b) => (b.strategy_id ?? "unknown") === strategyId);
  const wins   = my.filter((b) => b.outcome === "win").length;
  const losses = my.filter((b) => b.outcome === "loss").length;
  const decided = wins + losses;

  const clvs = my.map((b) => b.clv).filter((x): x is number => typeof x === "number");
  const avg_clv = clvs.length > 0 ? clvs.reduce((s, x) => s + x, 0) / clvs.length : null;

  const edges = my.map((b) => b.edge_pp).filter((x): x is number => typeof x === "number");
  const avg_edge = edges.length > 0 ? edges.reduce((s, x) => s + x, 0) / edges.length : null;

  const sports = Array.from(new Set(my.map((b) => (b.sport ?? "?").toUpperCase()))).sort();

  return {
    strategy_id: strategyId,
    bets:        my,
    decided, wins, losses,
    win_rate:    decided > 0 ? (wins / decided) * 100 : null,
    pnl_units:   Number(my.reduce((s, b) => s + (b.pnl ?? 0), 0).toFixed(2)),
    avg_clv,
    avg_edge,
    sports,
  };
}

// Two-proportion z-test on win rates. Standard form:
//   pooled_p = (x1 + x2) / (n1 + n2)
//   se       = sqrt(pooled_p * (1 - pooled_p) * (1/n1 + 1/n2))
//   z        = (p2 - p1) / se
//   p_two    = 2 * (1 - Φ(|z|))
function twoPropZTest(c: Variant, t: Variant): ZResult | null {
  const n1 = c.decided, x1 = c.wins;
  const n2 = t.decided, x2 = t.wins;
  if (n1 === 0 || n2 === 0) return null;

  const p1 = x1 / n1;
  const p2 = x2 / n2;
  const pooled = (x1 + x2) / (n1 + n2);
  const se = Math.sqrt(pooled * (1 - pooled) * (1 / n1 + 1 / n2));
  if (se === 0) return null;
  const z = (p2 - p1) / se;
  const p_two = 2 * (1 - normalCdf(Math.abs(z)));

  return {
    z,
    p_two,
    significant_05: p_two < 0.05,
    significant_01: p_two < 0.01,
    control_p:      p1 * 100,
    treatment_p:    p2 * 100,
    diff_pp:        (p2 - p1) * 100,
  };
}

// Standard normal CDF — Abramowitz & Stegun 26.2.17, accurate to ~7e-8
function normalCdf(x: number): number {
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p  = 0.3275911;
  const sign = x < 0 ? -1 : 1;
  const ax = Math.abs(x) / Math.SQRT2;
  const t = 1 / (1 + p * ax);
  const y = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-ax * ax);
  return 0.5 * (1 + sign * y);
}

// =============================================================
// Explain
// =============================================================
function buildExplainContext(c: Variant, t: Variant, z: ZResult | null): string {
  const lines = [
    `A/B comparator:`,
    `Control:   ${c.strategy_id} (${c.sports.join("/")}) — ${c.decided}D, ${c.wins}W ${c.losses}L, ${c.win_rate?.toFixed(1) ?? "—"}%, ${c.pnl_units >= 0 ? "+" : ""}${c.pnl_units}u`,
    `Treatment: ${t.strategy_id} (${t.sports.join("/")}) — ${t.decided}D, ${t.wins}W ${t.losses}L, ${t.win_rate?.toFixed(1) ?? "—"}%, ${t.pnl_units >= 0 ? "+" : ""}${t.pnl_units}u`,
  ];
  if (z) {
    lines.push(
      "",
      `Two-proportion z-test:`,
      `  diff: ${z.diff_pp >= 0 ? "+" : ""}${z.diff_pp.toFixed(2)}pp (treatment − control)`,
      `  z = ${z.z.toFixed(3)}`,
      `  p (two-sided) = ${z.p_two.toExponential(2)}`,
      `  significant at α=0.05: ${z.significant_05}`,
      `  significant at α=0.01: ${z.significant_01}`,
    );
  } else {
    lines.push("", "Cannot run z-test: at least one variant has zero decided bets.");
  }
  return lines.join("\n");
}

// =============================================================
// State views
// =============================================================
function skeletonView(): TemplateResult {
  return html`
    <div class="exp">
      <section class="panel" style="height:80px;"></section>
      <div class="exp-grid">
        <section class="panel" style="height:300px;"></section>
        <section class="panel" style="height:300px;"></section>
      </div>
    </div>
  `;
}

function emptyView(): TemplateResult {
  return html`
    <div class="panel" style="padding:48px;text-align:center;">
      <div style="color:var(--text-tertiary);">No bets yet — experiments need historical data.</div>
    </div>
  `;
}

function errorView(err: Error): TemplateResult {
  return html`
    <div class="panel" style="padding:32px;border-color:rgba(255,69,58,0.32);">
      <div class="panel__title">
        <span>Experiments</span>
        <span class="phase-stub-badge"
              style="background:rgba(255,69,58,0.16);
                      color:var(--accent-red-light);
                      border-color:rgba(255,69,58,0.32);">
          fetch error
        </span>
      </div>
      <div style="color:var(--accent-red-light);font-size:12px;
                   line-height:1.5;font-family:monospace;
                   word-break:break-all;white-space:pre-wrap;
                   margin-top:8px;">
        ${err.message}
      </div>
    </div>
  `;
}
